import { useState, useEffect, useContext, useCallback } from 'react';
import { toast } from 'react-toastify';
import webhooksService from '../api/webhooksService';
import { AuthContext } from '../context/AuthContextProvider';

const useWebhookConfiguration = () => {
  const { negocioActivo } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [webhook, setWebhook] = useState(null);
  const [isConfigured, setIsConfigured] = useState(false);

  const fetchWebhook = useCallback(async () => {
    setLoading(true);
    try {
      const response = await webhooksService.getWebhook();
      const data = response.data;
      setWebhook(data);
      setIsConfigured(data !== null);
    } catch (error) {
      console.error('Error fetching webhook configuration:', error);
      toast.error('Error al cargar la configuración del webhook');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (negocioActivo) {
      fetchWebhook();
    }
  }, [negocioActivo, fetchWebhook]);

  const saveWebhook = useCallback(async (webhookUrl) => {
    setSaving(true);
    try {
      if (isConfigured) {
        await webhooksService.updateWebhook({ webhookUrl });
        toast.success('Webhook actualizado exitosamente');
      } else {
        await webhooksService.createWebhook({ webhookUrl });
        toast.success('Webhook configurado exitosamente');
      }
      // Refrescar configuración
      const response = await webhooksService.getWebhook();
      const data = response.data;
      setWebhook(data);
      setIsConfigured(data !== null);
    } catch (error) {
      console.error('Error saving webhook:', error);
      toast.error('Error al guardar el webhook');
    } finally {
      setSaving(false);
    }
  }, [isConfigured]);

  const deleteWebhook = useCallback(async () => {
    const confirmed = window.confirm(
      '¿Estás seguro de que querés eliminar el webhook? Ya no recibirás eventos de Faster.'
    );
    
    if (!confirmed) return;

    setSaving(true);
    try {
      await webhooksService.deleteWebhook();
      toast.success('Webhook eliminado exitosamente');
      setWebhook(null);
      setIsConfigured(false);
    } catch (error) {
      console.error('Error deleting webhook:', error);
      toast.error('Error al eliminar el webhook');
    } finally {
      setSaving(false);
    }
  }, []);

  const getPartialSecret = () => {
    if (!webhook?.secret) return '';
    const secret = webhook.secret;
    if (secret.length <= 16) return secret;
    return `${secret.substring(0, 8)}...${secret.substring(secret.length - 8)}`;
  };

  return {
    loading,
    saving,
    webhook,
    isConfigured,
    isActive: webhook?.activo ?? false,
    fetchWebhook,
    saveWebhook,
    deleteWebhook,
    getPartialSecret,
  };
};

export default useWebhookConfiguration;

